import React from 'react'
import { Check, X, AlertTriangle } from 'lucide-react'

/**
 * Environment Configuration Checker
 * Helps users verify their Google OAuth setup
 */
const EnvironmentCheck = () => {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  const isDevelopment = import.meta.env.DEV
  
  const checks = [
    {
      name: 'Google Client ID',
      status: googleClientId && googleClientId !== 'your-google-client-id' && googleClientId.includes('.apps.googleusercontent.com') ? 'success' : 'error',
      message: googleClientId && googleClientId !== 'your-google-client-id' && googleClientId.includes('.apps.googleusercontent.com')
        ? `Configured: ${googleClientId.substring(0, 20)}...`
        : googleClientId ? 'Invalid Client ID format' : 'Not configured in environment',
      required: true
    },
    {
      name: 'Development Mode',
      status: isDevelopment ? 'success' : 'warning',
      message: isDevelopment ? 'Running in development mode' : 'Running in production mode',
      required: false
    },
    {
      name: 'HTTPS/Localhost',
      status: (window.location.protocol === 'https:' || window.location.hostname === 'localhost') ? 'success' : 'error',
      message: window.location.protocol === 'https:' || window.location.hostname === 'localhost'
        ? 'Secure origin detected'
        : 'Google OAuth requires HTTPS or localhost',
      required: true
    }
  ]

  const hasErrors = checks.some(check => check.required && check.status === 'error')

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Configuration Status</h4>
      
      <div className="space-y-2">
        {checks.map((check, index) => (
          <div key={index} className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
            <div className="flex-shrink-0">
              {check.status === 'success' && (
                <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
              )}
              {check.status === 'warning' && (
                <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              )}
              {check.status === 'error' && (
                <X className="w-4 h-4 text-red-600 dark:text-red-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{check.name}</p>
              <p className={`text-xs ${
                check.status === 'success' ? 'text-green-600 dark:text-green-400' :
                check.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' :
                'text-red-600 dark:text-red-400'
              }`}>
                {check.message}
              </p>
            </div>
          </div>
        ))}
      </div>

      {hasErrors && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>Configuration Issues Detected:</strong> Please complete the setup instructions above 
            to resolve these issues before attempting to connect to Google Drive.
          </p>
        </div>
      )}

      {!hasErrors && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
          <p className="text-sm text-green-800 dark:text-green-200">
            <strong>✓ Configuration Valid:</strong> Your Google Drive integration is properly configured 
            and ready to use!
          </p>
        </div>
      )}
    </div>
  )
}

export default EnvironmentCheck 